import { Injectable } from "@angular/core";
import {
  BfxBaseBlotterActionService,
  ActionListener,
} from "app/bfx-base-blotter/bfx-base-blotter-action.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "app/golden-layout/golden-layout-service";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { TranslateService } from "@ngx-translate/core";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { BmarkClientOrdersContextMenuService } from "./bmark-client-orders-context-menu.service";
import { DashboardItemType } from "app/bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "app/golden-layout/golden-layout-addition-mode";
import { BfxBlotterActionEvent } from "app/bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import { OENOperationMode } from "app/order-entry/data/enums/oe-operation-mode.enum";
import { OEUtilityService } from "app/order-entry/services/basic/oe-utility.service";
import { OESettingsService } from "app/order-entry/services/basic/oe-settings.service";
import {
  BfxBlotterAction,
  OrderDataActionPromiseService,
  OrderAcceptancePromiseService,
  BenchmarkDataPrimaryLookupService,
  BranchReplicaLookupService,
  NotificationService,
  BFXNotificationType,
  BFXNotificationSubType,
  BFXNotificationEventType,
} from "shared/cubiq-services";
import { OEApiService } from "app/order-entry/services/oe-api.service";
import { BfxOrderCancelComponentLauncher } from "app/bfx-order-cancel/bfx-order-cancel.launcher";
import { BmarkClientOrdersContextMenuItemState } from "../models/bmark-client-orders-context-menu.models";
import { BmarkClientOrdersDataService } from "./bmark-client-orders-data.service";
import { Subject } from "rxjs";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { SI_UNLOCK_STATES } from "../models/bmark-client-orders-context-menu.const";
import { BfxConfigIDRefHelper } from "../../../../../shared/helper-services/bfx-config-id-ref-helper";
import { BFXDialogIntent } from "app/bfx-dialogs/models/bfx-dialog-intent.enum";
import { ManualNettingAction } from "shared/cubiq-model-multi-asset/model/enum/ManualNettingAction";

@Injectable()
export class BfxBmarkClientOrdersBlotterActionsService extends BfxBaseBlotterActionService<any> {
  /* OBSERVABLE */
  // Observable string sources
  private bmarkActionDialogSource = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.bmarkActionDialogSource.asObservable();

  constructor(
    protected contextMenuService: BmarkClientOrdersContextMenuService,
    protected layoutService: GoldenLayoutService,
    protected translationService: TranslateService,
    protected configService: BfxConfigService,
    protected oeUtility: OEUtilityService,
    protected settings: OESettingsService,
    protected orderActionService: OrderDataActionPromiseService,
    private oeApi: OEApiService,
    private blotterOrdersService: BmarkClientOrdersDataService,
    private bmarkDataService: BenchmarkDataPrimaryLookupService,
    private orderAcceptanceService: OrderAcceptancePromiseService,
    private translate: TranslateService,
    protected notificationService: NotificationService,
  ) {
    super(contextMenuService, layoutService, translationService, configService);
  }

  /* ACCEPT */
  @ActionListener(BfxBlotterAction.ACTION_ORDER_ACCEPT)
  public onAccept(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BmarkClientOrdersContextMenuItemState;
    if (itemState.acceptanceReference instanceof Map && itemState.acceptanceReference.size > 0) {
      itemState.acceptanceReference.forEach((ids, acceptanceType) => {
        if (Array.isArray(ids) && ids.length > 0) {
          this.orderAcceptanceService.requestAcceptance(
            ids,
            "",
            CubiqTS.AcceptanceAction.ACCEPT,
            acceptanceType,
            null,
            null,
            CubiqTS.OrderType.BENCHMARK,
          );
        }
      });
    }
  }

  /* REJECT */
  @ActionListener(BfxBlotterAction.ACTION_ORDER_REJECT)
  public onTraderReject(event: BfxBlotterActionEvent) {
    let itemState = event.itemState as BmarkClientOrdersContextMenuItemState;
    if (itemState.acceptanceReference instanceof Map && itemState.acceptanceReference.size > 0) {
      itemState.acceptanceReference.forEach((ids, acceptanceType) => {
        if (Array.isArray(ids) && ids.length > 0) {
          this.orderAcceptanceService.requestAcceptance(
            ids,
            "",
            CubiqTS.AcceptanceAction.REJECT,
            acceptanceType,
            null,
            null,
            CubiqTS.OrderType.BENCHMARK,
          );
        }
      });
    }
  }

  /* VIEW ORDER DETAILS */
  @ActionListener(BfxBlotterAction.ACTION_VIEW_DETAILS)
  public onOrderDetails(event: BfxBlotterActionEvent) {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = false;
    config.sourceEvent = event.sourceEvent;
    config.ladderBlotter = event.ladderBlotter;

    config.data = {
      selectedIds: event.selectedIds,
      allocationId: event.selectedItems[0].allocationId,
    };

    let title: string = this.translationService.instant("order.details.title", [
      BfxConfigIDRefHelper.getOrderRef(event.selectedItems[0]),
    ]);

    this.layoutService.launchFromDashboard(
      title,
      DashboardItemType.OrderDetails,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  /* MODIFY */
  @ActionListener(BfxBlotterAction.ACTION_ORDER_MODIFY)
  public onOrderModify(event: BfxBlotterActionEvent) {
    this.oeApi.doOrderEntryPopupForOrderId(event.selectedIds[0], OENOperationMode.MODIFY); // this.apiService,
  }

  /* DUPLICATE */
  @ActionListener(BfxBlotterAction.ACTION_ORDER_DUPLICATE)
  public onOrderDuplicate(event: BfxBlotterActionEvent) {
    this.oeApi.doOrderEntryPopupForOrderId(event.selectedIds[0], OENOperationMode.DUPLICATE);
  }

  /* UNLOCK */
  @ActionListener(BfxBlotterAction.ACTION_ORDER_UNLOCK)
  public onOrderUnlock(event: BfxBlotterActionEvent) {
    let selectedIds: Array<string> = event.selectedIds;
    if (Array.isArray(selectedIds) && selectedIds.length > 0) {
      this.bmarkDataService
        .getBmarkOrderDataForOrderIds(selectedIds)
        .then((orderData: Array<BmarkOrderData>) => {
          let inModLockIds: Array<string> = new Array<string>();
          let inSiLockIds: Array<string> = new Array<string>();

          for (let selectedId of selectedIds) {
            let orderItem = orderData.find(
              (item) =>
                item.orderId instanceof CubiqTS.Long && item.orderId.toString() === selectedId,
            );
            if (orderItem instanceof BmarkOrderData) {
              if (SI_UNLOCK_STATES.includes(orderItem.orderState)) {
                inSiLockIds.push(selectedId);
              } else {
                inModLockIds.push(selectedId);
              }
            } else {
              logger.out.error(
                `ERROR: BfxBmarkClientOrdersBlotterActionsService.onOrderUnlock(): Order data not found for ${selectedId}`,
              );
            }
          }
          // Unlock MODIFY_LOCK
          if (Array.isArray(inModLockIds) && inModLockIds.length > 0) {
            this.orderActionService.unlockBenchmarks(inModLockIds).then(
              () => {},
              (err) => {
                logger.out.error(
                  `ERROR: BfxBmarkClientOrdersBlotterActionsService.onOrderUnlock(): Operation fail: `,
                  err,
                );
              },
            );
          }
          // Unlock SI_LOCK
          if (Array.isArray(inSiLockIds) && inSiLockIds.length > 0) {
            this.orderActionService.releaseOrder(inSiLockIds, CubiqTS.OrderType.BENCHMARK).then(
              () => {},
              (err) => {
                logger.out.error(
                  `ERROR: BfxBmarkClientOrdersBlotterActionsService.onOrderUnlock(): Operation fail: `,
                  err,
                );
              },
            );
          }
        });
    }
  }

  /* VIEW TRADE DETAILS */
  @ActionListener(BfxBlotterAction.ACTION_TRADE_VIEW_TRADE_DETAILS)
  public onViewTradeDetails(_event: BfxBlotterActionEvent) {
    if (_event.selectedItems[0] != null && typeof _event.selectedItems[0].dealId === "string") {
      let dealId = _event.selectedItems[0].dealId;
      let config: GLPopoutConfiguration = new GLPopoutConfiguration();
      config.initialState = GLPopoutState.POPOUT;
      config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
      config.popoutResizable = false;
      config.ladderBlotter = _event.ladderBlotter;
      config.data = { tradeDealId: dealId };
      config.sourceEvent = _event.sourceEvent;

      let title: string = "Trade Details"; // temp, will be replaced in component when deal is loaded

      this.layoutService.launchFromDashboard(
        title,
        DashboardItemType.DealDetails,
        true,
        GoldenLayoutAdditionMode.DEFAULT,
        config,
      );
    } else {
      logger.out.error(
        `ERROR: Invalid BfxBlotterActionEvent for ACTION_TRADE_VIEW_TRADE_DETAILS`,
        _event,
      );
    }
  }

  /* CANCEL */
  @ActionListener(BfxBlotterAction.ACTION_ORDER_CANCEL)
  public onCancel(event: BfxBlotterActionEvent): void {
    BfxOrderCancelComponentLauncher.launch(
      event.selectedItems,
      this.layoutService,
      this.translationService,
      event.sourceEvent,
    );
  }

  @ActionListener(BfxBlotterAction.ACTION_TAKE)
  public onTake(event: BfxBlotterActionEvent): void {
    let transferItems: Array<any> = [];

    if (event.selectedIds != null && event.selectedIds.length > 0) {
      let rawDataObject: any = this.blotterOrdersService.getRawDataObject();
      event.selectedIds.forEach((orderId: string) => {
        let item: any = rawDataObject[orderId];
        transferItems.push(item);
      });
    }

    event["transferItems"] = transferItems;

    let title: string = this.translationService.instant("take.order.title");

    let config: GLPopoutConfiguration = new GLPopoutConfiguration();

    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = true;
    config.preferredSize = {
      width: Math.max(750, window.innerWidth / 2),
      height: 389,
    };
    config.minimumSize = { width: 630, height: 389 };
    config.data = event.selectedItems;
    config.sourceEvent = event.sourceEvent;
    Object.defineProperty(config.data, "benchmarks", {
      value: true,
      enumerable: false,
    });

    this.layoutService.launchFromDashboard(
      title,
      DashboardItemType.OrderTake,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  /* EXPORT */
  @ActionListener(BfxBlotterAction.ACTION_TRADE_EXPORT)
  public onExport(event: BfxBlotterActionEvent) {
    event.selectedIds = [];
    event.selectedItems.forEach((order) => event.selectedIds.push(order.primaryKey));
    this.bmarkActionDialogSource.next(
      new BfxDialogData(BfxDialogCommand.DIALOG_CSV_EXPORT_OPEN, null, event, {
        blotterName: "ClientOrdersBlotter",
      }),
    );
  }

  @ActionListener(BfxBlotterAction.ACTION_TRANSFER)
  public onTransfer(event: BfxBlotterActionEvent): void {
    let transferItems: Array<any> = [];

    if (event.selectedIds != null && event.selectedIds.length > 0) {
      let rawDataObject: any = this.blotterOrdersService.getRawDataObject();
      event.selectedIds.forEach((orderId: string) => {
        let item: any = rawDataObject[orderId];
        transferItems.push(item);
      });
    }

    event["transferItems"] = transferItems;

    let title: string = this.translationService.instant("transfer.order.title");

    let config: GLPopoutConfiguration = new GLPopoutConfiguration();

    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = true;
    config.preferredSize = {
      width: Math.max(750, window.innerWidth / 2),
      height: 389,
    };
    config.minimumSize = { width: 630, height: 389 };
    config.data = event.selectedItems;
    Object.defineProperty(config.data, "benchmarks", {
      value: true,
      enumerable: false,
    });
    config.sourceEvent = event.sourceEvent;

    this.layoutService.launchFromDashboard(
      title,
      DashboardItemType.OrderTransfer,
      true,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  @ActionListener(BfxBlotterAction.ACTION_NET_ORDERS)
  public onNetOrders(event: BfxBlotterActionEvent) {
    this.executeNettingOperation(event);
  }

  @ActionListener(BfxBlotterAction.ACTION_NET_CLEAR)
  public onClearNetting(event: BfxBlotterActionEvent) {
    this.executeNettingOperation(event);
  }

  @ActionListener(BfxBlotterAction.ACTION_NET_DISABLE)
  public onDisableNetting(event: BfxBlotterActionEvent) {
    this.executeNettingOperation(event);
  }

  /**
   * Execute one of 3 possible netting operations based on the event.action value
   * @param event
   */
  private executeNettingOperation(event: BfxBlotterActionEvent) {
    const orderIds = this.getNettingOrderIds(event);
    const clientIds = this.getNettingClientIds(event);

    if (orderIds.length > 0) {
      // SEt up the action-specific titles
      const title = this.getNettingDialogTitleId(event.action);
      const message = this.getNettingDialogMessage(event.action, orderIds, clientIds);
      const actionLabel = this.getNettingActionLabel(event.action);
      const dialogData = new BfxDialogData(BfxDialogCommand.DIALOG_GENERIC_OK_CANCEL, null, null, {
        messages: [message],
        title: title,
        okLabel: actionLabel,
        intent: BFXDialogIntent.NORMAL,
        callback: () => {
          logger.out.info("Netting action confirmed by user");
          this.processNettingAction(event.action, orderIds);
        },
        callbackNegative: () => {
          logger.out.info("Netting action cancelled by user");
        },
      });
      this.bmarkActionDialogSource.next(dialogData);
    }
  }

  /**
   * Get a netting dialog title Id specific for the specified action
   * @param _actionId
   * @returns
   */
  private getNettingDialogTitleId(_actionId: BfxBlotterAction): string {
    return "netting.net.title";
  }

  /**
   * Get the netting dialog text message specific for the specified action
   * @param actionId
   * @param orderIds
   * @param clientIds
   * @returns
   */
  private getNettingDialogMessage(
    actionId: BfxBlotterAction,
    orderIds: string[],
    clientIds: string[],
  ): string {
    let message: string = "";
    switch (actionId) {
      case BfxBlotterAction.ACTION_NET_ORDERS:
        // Are you sure you want to Net (by client) {{0}} orders for client '{{1}}'?",
        message =
          clientIds.length === 1
            ? this.translate.instant("netting.net.orders.client.single", [
                orderIds.length,
                clientIds[0],
              ])
            : this.translate.instant("netting.net.orders.client.multi", [
                orderIds.length,
                clientIds.length,
              ]);
        break;
      case BfxBlotterAction.ACTION_NET_CLEAR:
        // Are you sure you want to Clear Netting (by client) for {{0}} orders?
        message =
          orderIds.length == 1
            ? this.translate.instant("netting.clear.orders.single")
            : this.translate.instant("netting.clear.orders.multi", [orderIds.length]);
        break;
      case BfxBlotterAction.ACTION_NET_DISABLE:
        // Are you sure you want to Disable Netting (by client) for 1 order?
        message =
          orderIds.length == 1
            ? this.translate.instant("netting.disable.orders.single")
            : this.translate.instant("netting.disable.orders.multi", [orderIds.length]);
        break;
    }
    return message;
  }

  /**
   * Get the netting OK button label for the specified action
   * @param actionId
   * @returns
   */
  private getNettingActionLabel(actionId: BfxBlotterAction): string {
    let actionLabelId: string = "";
    switch (actionId) {
      case BfxBlotterAction.ACTION_NET_ORDERS:
        actionLabelId = "netting.net.orders.action";
        break;
      case BfxBlotterAction.ACTION_NET_CLEAR:
        actionLabelId = "netting.clear.orders.action";
        break;
      case BfxBlotterAction.ACTION_NET_DISABLE:
        actionLabelId = "netting.disable.orders.action";
        break;
    }
    return this.translate.instant(actionLabelId);
  }

  /**
   * Extract array of unique order Ids from selected ids
   * @param event
   * @returns
   */
  private getNettingOrderIds(event: BfxBlotterActionEvent): string[] {
    if (event.selectedIds != null && event.selectedIds.length > 0) {
      const ids: Set<string> = new Set();
      event.selectedIds.forEach((id) => ids.add(id));
      return Array.from(ids);
    }
    return [];
  }

  /**
   * Extract array of unique client codes for selected items
   * @param event
   * @returns
   */
  private getNettingClientIds(event: BfxBlotterActionEvent): string[] {
    if (event.selectedItems != null && event.selectedItems.length > 0) {
      const ids: Set<string> = new Set();
      event.selectedItems.forEach((data: BmarkOrderData) => ids.add(data.clientCode));
      return Array.from(ids);
    }
    return [];
  }

  /**
   * Callback when user has accepted the netting action in the netting dialog
   * @param actionId
   * @param orderIds
   * @returns
   */
  private processNettingAction(actionId: BfxBlotterAction, orderIds: string[]) {
    let nettingAction: ManualNettingAction;
    switch (actionId) {
      case BfxBlotterAction.ACTION_NET_ORDERS:
        nettingAction = ManualNettingAction.NET;
        break;
      case BfxBlotterAction.ACTION_NET_CLEAR:
        nettingAction = ManualNettingAction.CLEAR;
        break;
      case BfxBlotterAction.ACTION_NET_DISABLE:
        nettingAction = ManualNettingAction.DISABLE;
        break;
      default:
        logger.out.warn("bm-clact-s - Unknown action");
        return;
    }
    this.orderActionService.netBenchmarkOrders(nettingAction, orderIds).then(
      (result: CubiqTS.ManualNettingResponseImpl) => {
        if (result?.success) {
          logger.out.info(`bm-clact-s - Netting operation completed successfully`);
          return;
        }

        if (
          result?.errors instanceof CubiqTS.ErrorsImpl &&
          Array.isArray(result?.errors?.errorMessages)
        ) {
          let hcoOrderRefs = new Set<string>();
          let allHCO = result.errors.errorMessages.every((value: any) => {
            let errMsg =
              value != null && typeof value === "string"
                ? JSON.parse(value)
                : { id: null, args: [] };

            if (errMsg?.id === "modification.not.to.benchmark.in.hard.cut.off") {
              hcoOrderRefs.add(errMsg.args[0]);
              return true;
            }
          });

          logger.out.warn(`bm-clact-s - Netting operation failed`);

          if (!allHCO) {
            this.notificationService.logGenericErrorMessages(
              result.errors,
              "netting.operation.error",
            );
            // "overridden by another netting operation." is not emitted by server
            this.notificationService.handleGenericError(
              result.errors,
              "",
              "netting.operation.error",
            );
            return;
          }

          let title: string = this.translate.instant("netting.failed.title");
          let msg = ``;
          let ids = [...hcoOrderRefs];

          if (allHCO && hcoOrderRefs.size == 2) {
            msg = this.translate.instant("netting.failed.msg.double", ids);
          } else if (allHCO && hcoOrderRefs.size == 3) {
            msg = this.translate.instant("netting.failed.msg.triple", ids);
          } else if (allHCO && hcoOrderRefs.size >= 3) {
            msg = this.translate.instant("netting.failed.msg.multi", [
              ids.pop(),
              ids.pop(),
              ids.pop(),
              ids.length,
            ]);
          }
          this.notificationService.fireNotificationEvent(
            this.notificationService.simpleMessageToNotification(
              msg,
              BFXNotificationType.NETTING_FAILED,
              BFXNotificationSubType.NETTING_FAILED,
              this.translationService.instant(title),
            ),
            BFXNotificationEventType.MESSAGE,
          );
        }
      },
      (result: any) => {
        logger.out.error(
          `bm-clact-s - Error performing action ${BfxBlotterAction[actionId]} Reason: ${result}`,
        );
        const title: string = this.translate.instant("netting.operation.error");
        if (!(result instanceof CubiqTS.ErrorsImpl)) {
          result = new Error(result?.toString() ?? "<UNKNOWN>");
        }
        this.notificationService.handleGenericError(result, null, title);
      },
    );
  }
}
